import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { DEFAULT_LOCALE, intlLocale, type Locale } from './locales';
import { readStoredLocale, writeStoredLocale } from './storage';
import { createTranslator, type Translate } from './translate';

export interface I18nContextValue {
  locale: Locale;
  /** BCP 47 tag for Intl APIs. */
  intlLocale: string;
  setLocale: (locale: Locale) => void;
  /** True when the user explicitly chose a language on this device. */
  hasStoredPreference: boolean;
  t: Translate;
}

function makeValue(
  locale: Locale,
  setLocale: (l: Locale) => void,
  hasStoredPreference: boolean,
): I18nContextValue {
  return {
    locale,
    intlLocale: intlLocale(locale),
    setLocale,
    hasStoredPreference,
    t: createTranslator(locale),
  };
}

/** Default (no provider) = English, so shared components render in isolation and tests. */
const I18nContext = createContext<I18nContextValue>(
  makeValue(DEFAULT_LOCALE, () => undefined, false),
);

export interface I18nProviderProps {
  children: ReactNode;
  /** Locale for the first render. SSR apps must keep this deterministic (hydration). */
  initialLocale?: Locale;
  /**
   * `sync` reads the stored choice during the first render (client-only SPAs/PWAs).
   * `after-mount` reads it in an effect, keeping SSR hydration stable (TanStack Start).
   * `none` disables persistence.
   */
  persistence?: 'sync' | 'after-mount' | 'none';
}

export function I18nProvider({
  children,
  initialLocale = DEFAULT_LOCALE,
  persistence = 'sync',
}: I18nProviderProps) {
  const [state, setState] = useState(() => {
    const stored = persistence === 'sync' ? readStoredLocale() : null;
    return { locale: stored ?? initialLocale, stored: stored !== null };
  });

  useEffect(() => {
    if (persistence !== 'after-mount') return;
    const stored = readStoredLocale();
    // eslint-disable-next-line react-hooks/set-state-in-effect -- hydrate client-only preference after SSR
    if (stored) setState({ locale: stored, stored: true });
  }, [persistence]);

  useEffect(() => {
    document.documentElement.lang = state.locale;
  }, [state.locale]);

  const setLocale = useCallback(
    (next: Locale) => {
      if (persistence !== 'none') writeStoredLocale(next);
      setState({ locale: next, stored: true });
    },
    [persistence],
  );

  const value = useMemo(() => makeValue(state.locale, setLocale, state.stored), [state, setLocale]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  return useContext(I18nContext);
}

/** Shorthand when only `t` is needed. */
export function useT(): Translate {
  return useContext(I18nContext).t;
}
